import React from 'react'

import { useTranslation } from 'react-i18next'

// MUI Imports
import {
  Typography,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  Chip
} from '@mui/material'
import {
  MailOutline,
  Add,
  Cached,
  AttachFile,
  Reply as ReplyIcon,
  FirstPage,
  LastPage,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material'

// Component Imports
import LoadingOverlay from '../atomic/atoms/LoadingOverlay'
import VirtualList from '../atomic/organisms/VirtualList'

import { extractHtmlContent, getInitials } from '../../utils/helpers'
import McPageHelpers from '../../../../pages/mc-components/McPageHelpers'
import { last } from 'lodash'

const getDeepestMessage = (message) => {
  if (message.replies && message.replies.length > 0) {
    return getDeepestMessage(last(message.replies))
  }
  return message
}

const getRelativeTime = (millis, t) => {
  if (!millis || millis <= 0) return t('mailTemplate.mailbox.noDate')
  const now = Date.now()
  const diff = now - millis
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('mailTemplate.mailbox.time.now', 'Just now')
  if (minutes < 60) return t('mailTemplate.mailbox.time.minutesAgo', '{{count}}m ago', { count: minutes })
  if (hours < 24) return t('mailTemplate.mailbox.time.hoursAgo', '{{count}}h ago', { count: hours })
  if (days < 7) return t('mailTemplate.mailbox.time.daysAgo', '{{count}}d ago', { count: days })

  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  return new Intl.DateTimeFormat(userLocale, {
    month: 'short',
    day: 'numeric'
  }).format(new Date(millis))
}

const PAGE_SIZE_OPTIONS = [10, 25, 50]

const formatCount = (count) => {
  const userLocale = McPageHelpers.localeMap[localStorage.getItem('lang')] || 'en-US'

  return new Intl.NumberFormat(userLocale).format(count)
}

const countTotalMessages = (emailArr) => {
  if (!emailArr) return 0

  return emailArr.length
}

const hasAttachments = (emailArr) => {
  if (!emailArr) return false

  return emailArr.some(msg => msg?.attachments?.length > 0)
}

const EmailListItem = ({ emailArr, isActive, onClick, t }) => {
  const mainEmail = emailArr?.[0]
  const latestMessage = getDeepestMessage(mainEmail)
  const isIncoming = latestMessage?.type === 'INCOMING'

  const displayName = isIncoming
    ? (latestMessage.from_name || t('mailTemplate.mailbox.unknownSender'))
    : (latestMessage?.to?.[0]?.name || t('mailTemplate.mailbox.unknownRecipient'))

  const subject = mainEmail?.subject || t('mailTemplate.mailbox.noSubject')

  const bodyPreview = extractHtmlContent(mainEmail?.bodyHtml) || ''

  const messageCount = countTotalMessages(emailArr)
  const showAttachment = hasAttachments(emailArr)
  const timestamp = mainEmail?.sentTimeMillis

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: 'pointer',
        position: 'relative',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        bgcolor: '#fff',
        transition: 'background-color 0.15s ease',
        '&:hover': {
          bgcolor: '#f5f5f5'
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          borderRadius: '0 2px 2px 0',
          bgcolor: isActive
            ? 'primary.main'
            : 'transparent'
        }
      }}
    >
      {/* Avatar */}
      <Box sx={{
        width: 40,
        height: 40,
        borderRadius: '50%',
        bgcolor: 'primary.50',
        color: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.8rem',
        flexShrink: 0,
        mt: 0.25
      }}>
        {getInitials(displayName)}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        {/* Top row: name + time */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
          <Typography sx={{
            fontWeight: 600,
            fontSize: '0.85rem',
            lineHeight: 1.3,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {displayName}
          </Typography>
          <Typography sx={{
            fontSize: '0.7rem',
            color: 'text.disabled',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            {getRelativeTime(timestamp, t)}
          </Typography>
        </Box>

        {/* Subject */}
        <Typography sx={{
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          mb: 0.25
        }}>
          {subject}
        </Typography>

        {/* Body preview */}
        <Typography sx={{
          fontSize: '0.75rem',
          color: 'text.disabled',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          lineHeight: 1.4,
          mb: 0.5
        }}>
          {bodyPreview}
        </Typography>

        {/* Bottom row: badges */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {messageCount > 1 && (
            <Chip
              size="small"
              icon={<ReplyIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={messageCount}
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.5, mr: -0.25, color: 'text.disabled' }
              }}
            />
          )}
          {showAttachment && (
            <AttachFile sx={{ fontSize: '0.85rem', color: 'text.disabled', transform: 'rotate(45deg)' }} />
          )}
        </Box>
      </Box>
    </Box>
  )
}

const EmptyState = ({ setOpenCompose, t }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    p: 4
  }}>
    <Box sx={{
      maxWidth: 360,
      textAlign: 'center',
      p: 5
    }}>
      <Box sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mx: 'auto',
        mb: 2.5
      }}>
        <MailOutline sx={{ fontSize: 36, color: 'text.disabled' }} />
      </Box>

      <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', mb: 1, color: 'text.primary' }}>
        {t('mailTemplate.mailbox.list.noData')}
      </Typography>

      <Typography sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.6, fontSize: '0.85rem' }}>
        {t('mailTemplate.mailbox.list.noDataDescription')}
      </Typography>

      {setOpenCompose && (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenCompose(true)}
          sx={{
            borderRadius: 2,
            height: 38,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.85rem'
          }}
        >
          {t('mailTemplate.mailbox.list.noDataAction')}
        </Button>
      )}
    </Box>
  </Box>
)

const MailContentList = props => {
  const {
    fetching,
    emailArrays,
    activeEmailArr,
    setActiveEmailArr,
    setOpenCompose,
    reloadMessages,
    currentPage,
    pageSize,
    totalPageCount,
    totalRecordCount,
    onPageChange,
    onPageSizeChange
  } = props

  const { t } = useTranslation()
  const isEmpty = !emailArrays.length

  const pageIndex = currentPage ?? 0
  const isFirstPage = pageIndex <= 0
  const isLastPage = pageIndex >= totalPageCount - 1

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: '#fff',
        flexShrink: 0
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.95rem',
            fontWeight: 700,
            lineHeight: 1.3,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {t('mailTemplate.mailbox.inboxTitle')}
          </Typography>
          <Typography sx={{
            fontSize: '0.72rem',
            color: 'text.disabled',
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {t('mailTemplate.mailbox.list.conversationCount', {
              count: totalRecordCount ?? 0,
              formattedCount: formatCount(totalRecordCount ?? 0)
            })}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Tooltip title={t('mailTemplate.mailbox.send.composeTitle')} arrow>
            <IconButton
              size="small"
              onClick={() => setOpenCompose(true)}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                width: 32,
                height: 32,
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <Add sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('mailTemplate.mailbox.refresh', 'Refresh')} arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => reloadMessages()}
                disabled={fetching}
                sx={{ width: 32, height: 32 }}
              >
                <Cached sx={{ fontSize: 20 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Email list */}
      <Box sx={{ flex: 1, position: 'relative', bgcolor: '#fff', overflow: 'hidden' }}>
        {fetching
          ? <LoadingOverlay />
          : isEmpty
            ? (
                <EmptyState
                  setOpenCompose={setOpenCompose}
                  t={t}
                />
              )
            : (
                <VirtualList
                  items={emailArrays}
                  estimateSize={150}
                  overscan={5}
                  getItemKey={(emailArr, index) =>
                    emailArr?.[0]?.messageId || `email-${index}-${emailArr?.[0]?.sentTimeMillis}`}
                  renderItem={({ item: emailArr }) => (
                    <EmailListItem
                      emailArr={emailArr}
                      isActive={activeEmailArr === emailArr}
                      onClick={() => setActiveEmailArr(emailArr)}
                      t={t}
                    />
                  )}
                />
              )
        }
      </Box>

      {/* Pagination */}
      {!isEmpty && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
          flexShrink: 0,
          px: 1.5,
          py: 0.75
        }}>
          <FormControl size="small" sx={{ flexShrink: 0 }}>
            <Select
              value={pageSize ?? PAGE_SIZE_OPTIONS[0]}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={fetching}
              MenuProps={{ MenuListProps: { dense: true } }}
              sx={{
                height: 32,
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'text.secondary',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: 'unset !important',
                  py: 0,
                  pl: 1.25,
                  pr: '38px !important'
                },
                // The theme pins the arrow 1rem from the edge as a sibling svg, which
                // both crowds the value and leaves dead space against the border
                '& .MuiSelect-select ~ svg': {
                  right: '10px !important',
                  color: 'text.disabled'
                },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'text.disabled' }
              }}
            >
              {PAGE_SIZE_OPTIONS.map(option => (
                <MenuItem key={option} value={option} sx={{ fontSize: '0.78rem' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.25
          }}>
            <Tooltip title={t('mailTemplate.mailbox.list.firstPage')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPageChange(0)}
                  disabled={fetching || isFirstPage}
                  sx={{ width: 30, height: 30 }}
                >
                  <FirstPage sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('mailTemplate.mailbox.list.previousPage')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPageChange(pageIndex - 1)}
                  disabled={fetching || isFirstPage}
                  sx={{ width: 30, height: 30 }}
                >
                  <ChevronLeft sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <Typography sx={{
              px: 0.75,
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {t('mailTemplate.mailbox.list.pageOf', {
                page: pageIndex + 1,
                total: Math.max(totalPageCount, 1)
              })}
            </Typography>

            <Tooltip title={t('mailTemplate.mailbox.list.nextPage')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPageChange(pageIndex + 1)}
                  disabled={fetching || isLastPage}
                  sx={{ width: 30, height: 30 }}
                >
                  <ChevronRight sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('mailTemplate.mailbox.list.lastPage')} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onPageChange(totalPageCount - 1)}
                  disabled={fetching || isLastPage}
                  sx={{ width: 30, height: 30 }}
                >
                  <LastPage sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default MailContentList
